import React, { useState } from "react";
import dayjs from "dayjs";
import relativeTime from "dayjs/plugin/relativeTime";
import { AlertCircle } from "lucide-react";
import { useAlerts } from "../../../../hooks/useAlerts";
import { joinWithAnd } from "@/utils/helperFunctions";

dayjs.extend(relativeTime);

const AlertsList = ({ filteredAlerts, studentEmailToNameMap, chosenClass }) => {
  const [showAll, setShowAll] = useState(false);
  const { markAllAlertsAsRead, isLoading } = useAlerts();
  const [isMarkingAllRead, setIsMarkingAllRead] = useState(false);
  const [errorMessage, setErrorMessage] = useState(null);

  const handleMarkAllRead = async (alert) => {
    if (!filteredAlerts?.length) return;

    try {
      setIsMarkingAllRead(true);
      setErrorMessage(null);
      await markAllAlertsAsRead(alert?.wellness?.userId);
    } catch (error) {
      setErrorMessage("Failed to mark all alerts as read. Please try again.");
    } finally {
      setIsMarkingAllRead(false);
    }
  };

  if (!filteredAlerts?.length) {
    return (
      <div className="p-4 text-gray-500 flex flex-row items-center gap-4">
        <AlertCircle className="w-8 h-8 text-blue-600" />
        No notifications to display.
      </div>
    );
  }

  const sortedAlerts = [...filteredAlerts].sort(
    (a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime,
  );

  const displayedAlerts = showAll ? sortedAlerts : sortedAlerts.slice(0, 3);
  const hasUnreadAlerts = filteredAlerts.some((alert) => !alert.read);

  return (
    <div
      className={`px-3 pb-4 ${
        showAll ? "max-h-96 overflow-y-scroll" : "none"
      } `}
    >
      <div className="flex flex-row items-center justify-between pb-3 pt-3 sticky top-0 bg-white z-10">
        <span className="text-base sm:text-lg font-semibold ">
          Notifications
        </span>
        <button
          className={`text-sm w-[100px] text-right ${
            isMarkingAllRead || !hasUnreadAlerts
              ? "text-gray-400 cursor-not-allowed"
              : "text-[#1A6BE5] hover:text-[#1a4d7a]"
          } transition-colors duration-200`}
          onClick={() => {
            handleMarkAllRead(displayedAlerts[0]);
          }}
          disabled={isMarkingAllRead || !hasUnreadAlerts || isLoading}
        >
          {isMarkingAllRead ? "Updating..." : "Mark All Read"}
        </button>
      </div>
      {errorMessage && (
        <div className="text-red-500 text-[14px] mb-3">{errorMessage}</div>
      )}
      {displayedAlerts.map((alert) => {
        const studentName =
          studentEmailToNameMap?.[alert.email] || alert.studentId;
        const typeLabel =
          alert.type.kind === "word" ? "Word Alert" : "Emotion Pattern";
        const bgColor = alert.read ? "bg-white" : "bg-blue-50";

        return (
          <div
            key={alert._id}
            className={`flex flex-row items-start border-t border-[#DDDDDD] shadow-sm p-3 ${bgColor}`}
          >
            <AlertCircle className="w-4 h-4 sm:w-5 sm:h-5 text-[#1A6BE5]" />
            <div className="flex flex-col ml-3 w-full">
              <div className="mb-1 flex gap-3 items-center">
                <span className="text-[16px] font-semibold text-700">
                  {studentName}
                </span>
                <span className="text-[12px] text-gray-400">
                  {dayjs(alert.createdAt).fromNow()}
                </span>
              </div>
              {alert.type.kind === "word" ? (
                <div className="text-[14px] mb-1 opacity-75">
                  <span className="font-semibold">{typeLabel}:</span> Used the
                  word "
                  {Array.isArray(alert.type.value)
                    ? joinWithAnd(alert.type.value)
                    : alert.type.value}
                  "
                </div>
              ) : (
                <div className="text-[14px] mb-1 opacity-75">
                  <span className="font-semibold">{typeLabel}:</span>{" "}
                  <span className="capitalize">
                    {alert.type.value.split(",").join(", ")}
                  </span>{" "}
                  pattern detected
                </div>
              )}
              <div className="text-[12px] text-gray-600">
                {chosenClass || "- -"}
              </div>
            </div>
          </div>
        );
      })}

      {!showAll && filteredAlerts.length > 3 && (
        <div className="flex items-center justify-center rounded-b shadow-sm border-t border-[#DDDDDD]">
          <button
            onClick={() => setShowAll(true)}
            className="text-[16px] text-[#1A6BE5] font-semibold p-4"
          >
            View All Notifications
          </button>
        </div>
      )}
    </div>
  );
};

export default AlertsList;
